import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createTerm, qk } from 'api'
import { DialogContent, ToastContent } from 'elements/feedback'
import { useBoolean } from 'lib/hooks'
import { useProjectDetailsContext } from 'pages/projects/details/project-details-layout'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { TermForm } from '../term-form'

const formId = 'CREATE_TERM_FORM'

type CreateTermFormProps = {
  projectId: string
  closeDialog: () => void
}

export const CreateTermDialog = ({
  projectId,
  closeDialog,
}: CreateTermFormProps) => {
  const { t } = useTranslation()

  const { project } = useProjectDetailsContext()

  const saveAndContinue = useBoolean()
  const resetForm = useBoolean()

  const queryClient = useQueryClient()

  const $createTerm = useMutation(createTerm)

  return (
    <DialogContent
      title={t('projects.terms.create_term')}
      isActionLoading={$createTerm.isLoading}
      onDeny={closeDialog}
      externalFormId={formId}
      extraConfirmButtonText={t('projects.terms.save_and_create_new')}
      extraConfirmButtonAction={saveAndContinue.setTrue}
      confirmText={t('common.save')}
    >
      <TermForm
        formId={formId}
        projectId={projectId}
        resetForm={resetForm.isTrue}
        onReset={resetForm.setFalse}
        defaultValues={{
          key: '',
          description: '',
          tags: [],
          translations: project.languages.map(lang => ({ lang, text: '' })),
          link: false,
        }}
        onSubmit={values =>
          $createTerm.mutate(
            { projectId, ...values },
            {
              onSuccess: () => {
                queryClient.invalidateQueries(qk.terms.list.toKey())

                toast.success(
                  <ToastContent
                    title={t('projects.terms.term_successfully_created')}
                  />,
                )

                if (saveAndContinue.isTrue) {
                  resetForm.setTrue()
                  $createTerm.reset()
                  saveAndContinue.setFalse()
                } else {
                  closeDialog()
                }
              },
            },
          )
        }
      />
    </DialogContent>
  )
}
